import { useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, GraduationCap, Quote } from "lucide-react";
import { Reveal, EASE } from "./common";
import { TChapter, useLang, t } from "../i18n";
import { APPOINTMENTS, EDUCATION } from "../data/cv";

const THESES = [
  { label: "P.D.F. Topic — BHU", text: "CHUNAV: Citizens Help Using Navigational AI in Voting — Leveraging AI and Big Data Analytics for Mapping Electoral Behavior & Digital Voter Empowerment", year: "2026" },
  { label: "Ph.D. Dissertation — Amity", text: "Stability of Governments under Coalition Politics in Bihar: A Comparative Study of 2010–2015 State Assembly Elections", year: "2023" },
  { label: "M.A. (Edu.) Thesis — IGNOU", text: "A Comparative Study of Plato's Education System and National Education Policy 2020", year: "2025" },
];

const Journey = () => {
  const { lang } = useLang();
  const [track, setTrack] = useState("career");
  const items = track === "career" ? APPOINTMENTS : EDUCATION;

  return (
    <section id="journey" className="relative bg-bone bg-grid-light py-24 sm:py-36" data-testid="section-journey">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <TChapter num="03" k="c3" />

        <div className="grid sm:grid-cols-3 gap-4 mb-16">
          {THESES.map((t, i) => (
            <Reveal key={t.label} delay={i * 0.1} className="relative bg-ink text-bone rounded-2xl p-6 sm:p-7 overflow-hidden">
              <Quote size={60} className="absolute -top-2 -right-2 text-bone/[0.06]" />
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-saffron mb-3">{t.label} · {t.year}</p>
              <p className="font-display text-base sm:text-lg leading-relaxed italic text-bone/90">{t.text}</p>
            </Reveal>
          ))}
        </div>

        <div className="flex gap-2 mb-12" data-testid="journey-track-toggle">
          {[
            { key: "career", labelKey: "journey_career", icon: Briefcase },
            { key: "education", labelKey: "journey_education", icon: GraduationCap },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = track === tab.key;
            return (
              <button
                key={tab.key}
                data-testid={`journey-tab-${tab.key}`}
                onClick={() => setTrack(tab.key)}
                className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] px-6 py-3 rounded-full border transition-all duration-300 ${
                  active ? "bg-crimson text-bone border-crimson" : "bg-panel/70 text-ink/70 border-ink/15 hover:border-ink/40"
                }`}
              >
                <Icon size={13} /> {t(lang, tab.labelKey)}
              </button>
            );
          })}
        </div>

        <div className="relative pl-8 sm:pl-12" data-testid="journey-timeline">
          <div className="absolute left-[7px] sm:left-[11px] top-2 bottom-2 w-px bg-ink/15" />
          {items.map((item, i) => (
            <motion.div
              key={`${track}-${i}`}
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}
              className="relative pb-12 last:pb-0"
              data-testid={`journey-item-${track}-${i}`}
            >
              <span className={`absolute -left-8 sm:-left-12 top-1.5 w-[15px] h-[15px] sm:w-[23px] sm:h-[23px] rounded-full border-[3px] ${
                item.current ? "bg-crimson border-crimson/30 pulse-dot" : "bg-bone border-ink/30"
              }`} />
              <div className="grid sm:grid-cols-[180px_1fr] gap-1 sm:gap-8">
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-crimson pt-1">
                  {item.period || item.year}
                </span>
                <div>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-ink flex flex-wrap items-center gap-3">
                    {item.role || item.degree}
                    {item.current && (
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] bg-emerald/10 text-emerald px-2.5 py-1 rounded-full">Current</span>
                    )}
                  </h3>
                  <p className="text-sm font-semibold text-mutedink mt-1">{item.org}</p>
                  {(item.detail || item.note) && (
                    <p className="text-sm text-mutedink/80 mt-2 leading-relaxed">{item.detail || item.note}</p>
                  )}
                  {item.score && (
                    <span className="inline-block mt-3 font-mono text-[10px] uppercase tracking-[0.15em] bg-saffron/10 text-saffron px-3 py-1.5 rounded-full">
                      {item.score}
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Journey;
